/** A level of the company's performance scale, seen only by the floor it starts at. */
interface ScoredLevel {
    /** Lowest score the level covers. A level without one has no floor to reach, so it never is. */
    minScore?: number;
}

export interface LevelForScoreOptions<TLevel extends ScoredLevel> {
    /** The 0-100 score to place on the scale. */
    score: number;
    levels: TLevel[];
}

/**
 * The level a score reaches: among the levels whose `minScore` is at most `score`, the one with the
 * greatest `minScore`. This is the server's own rule (`PerformanceScoreLevelResolver`), so a score
 * the server names and a score named here read the same.
 *
 * `undefined` when no floor is met -- an empty scale, or every floor above the score. Naming a
 * level anyway would state a result the company's scale does not define.
 */
export const levelForScore = <TLevel extends ScoredLevel>({
    score,
    levels,
}: LevelForScoreOptions<TLevel>): TLevel | undefined => {
    let reached: { level: TLevel; minScore: number } | undefined;

    for (const level of levels) {
        const { minScore } = level;

        if (minScore === undefined || minScore > score) {
            continue;
        }

        if (!reached || minScore > reached.minScore) {
            reached = { level, minScore };
        }
    }

    return reached?.level;
};
